import { ADMIN_VIEW_PREFIX } from './routes'

/**
 * Builder único das URLs de curso/aula/extras.
 *
 * O aluno navega sob /enrollments/:enrollmentId; o produtor em preview não tem
 * matrícula e navega sob /admin_view. É o mesmo desvio que o core faz no
 * legado (routes_filter: `enrollment_id ? "/enrollments/#{id}" : "/admin_view"`).
 * Concentrar aqui evita que um link solto jogue o produtor numa rota de aluno,
 * onde o GraphQL responderia unauthorized.
 */
/**
 * useLocalSearchParams devolve `string | string[]`, então os call sites
 * repassam esse tipo. Normalizamos aqui em vez de espalhar cast por tela.
 */
type PathParam = string | string[] | number | null | undefined

const segment = (value: PathParam) =>
  Array.isArray(value) ? value[0] : `${value ?? ''}`

interface ICoursePathParams {
  isPreview?: boolean
  enrollmentId?: PathParam
  courseId: PathParam
}

interface ICourseContentPathParams extends ICoursePathParams {
  courseContentId: PathParam
  moduleId?: PathParam
}

export const homePath = (isPreview?: boolean) =>
  isPreview ? ADMIN_VIEW_PREFIX : '/'

const basePath = ({
  isPreview,
  enrollmentId,
}: Omit<ICoursePathParams, 'courseId'>) =>
  isPreview ? ADMIN_VIEW_PREFIX : `/enrollments/${segment(enrollmentId)}`

export const courseBasePath = ({
  isPreview,
  enrollmentId,
  courseId,
}: ICoursePathParams) =>
  `${basePath({ isPreview, enrollmentId })}/courses/${segment(courseId)}`

/**
 * Trilha (combo). O aluno chega em /enrollments/:id/paths/:pathId; no preview
 * não há matrícula e a trilha é lida direto (adminPreviewPath).
 */
export const comboPath = ({
  isPreview,
  enrollmentId,
  pathId,
}: Omit<ICoursePathParams, 'courseId'> & { pathId: PathParam }) =>
  `${basePath({ isPreview, enrollmentId })}/paths/${segment(pathId)}`

export const coursePath = (params: ICoursePathParams) =>
  params.isPreview
    ? courseBasePath(params)
    : `${courseBasePath(params)}/online_course`

export const extraContentsPath = (params: ICoursePathParams) =>
  params.isPreview
    ? `${courseBasePath(params)}/extra_contents`
    : `${courseBasePath(params)}/online_course/extra_contents`

export const courseContentPath = ({
  courseContentId,
  moduleId,
  ...params
}: ICourseContentPathParams) => {
  const suffix = `course_contents/${segment(courseContentId)}`
  const base = params.isPreview
    ? `${courseBasePath(params)}/${suffix}`
    : `${courseBasePath(params)}/online_course/${suffix}`

  return moduleId ? `${base}?moduleId=${segment(moduleId)}` : base
}

/**
 * URL da tela de consumo no hero_theme (escola sem a flag de aula no members).
 * Não existe em preview: quem chega aqui já está no members.
 */
export const heroThemeCourseContentPath = ({
  enrollmentId,
  courseId,
  courseContentId,
}: Omit<ICourseContentPathParams, 'isPreview' | 'moduleId'>) =>
  `/enrollments/${segment(enrollmentId)}/courses/${segment(courseId)}` +
  `/course_contents/${segment(courseContentId)}`
